import { supabase } from '@/services/supabase';
import { platformAdminService } from '@/services/platformAdminService';

/**
 * Impersonation flow helpers.
 *
 * The flow is intentionally low-level: we save the admin's session in
 * `sessionStorage` BEFORE redirecting to the magic link, so that after
 * Supabase swaps the session for the target user we can still find our
 * way back. Use the keys exported below if you need to read the state
 * directly (e.g. to render a banner).
 */

export const IMPERSONATION_KEYS = {
  refreshToken: 'admin_refresh_token',
  accessToken: 'admin_access_token',
  userId: 'impersonating_user_id',
  userLabel: 'impersonating_user_label',
};

/**
 * Returns true if the current browser session is an active impersonation.
 */
export function isImpersonating() {
  if (typeof window === 'undefined') return false;
  return !!window.sessionStorage.getItem(IMPERSONATION_KEYS.userId);
}

/**
 * The user-facing label for the impersonated user (set when the flow starts).
 */
export function impersonatingUserLabel() {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(IMPERSONATION_KEYS.userLabel);
}

/**
 * Start an impersonation session.
 *
 * 1. Save the current admin's tokens so we can restore them later.
 * 2. Ask the backend for a Supabase magic link for the target user.
 * 3. Redirect — Supabase handles the actual session swap.
 *
 * NOTE: this function navigates the browser; it never resolves on success.
 *
 * @param {string} userId UUID of the user to impersonate
 * @param {string} [label] Display label for the banner (e.g. "cas@example.com")
 * @param {string} [redirectTo] Path to land on after the magic-link redirect (default `/`)
 */
export async function startImpersonation(userId, label = '', redirectTo = '/') {
  const { data } = await supabase.auth.getSession();
  if (data?.session) {
    window.sessionStorage.setItem(
      IMPERSONATION_KEYS.refreshToken,
      data.session.refresh_token
    );
    window.sessionStorage.setItem(
      IMPERSONATION_KEYS.accessToken,
      data.session.access_token
    );
  }
  window.sessionStorage.setItem(IMPERSONATION_KEYS.userId, userId);
  if (label) {
    window.sessionStorage.setItem(IMPERSONATION_KEYS.userLabel, label);
  }

  const result = await platformAdminService.startImpersonation(userId, redirectTo);
  if (!result?.magicLinkUrl) {
    throw new Error('No magic link returned from /platform/users/:id/impersonate');
  }
  window.location.href = result.magicLinkUrl;
}

/**
 * End an impersonation session: log it server-side, restore the admin tokens,
 * then bounce back to the platform admin landing page.
 */
export async function endImpersonation() {
  const refreshToken = window.sessionStorage.getItem(IMPERSONATION_KEYS.refreshToken);
  const userId = window.sessionStorage.getItem(IMPERSONATION_KEYS.userId);

  if (userId) {
    try {
      await platformAdminService.endImpersonation(userId);
    } catch (error) {
      console.error('Failed to audit-log impersonation end:', error);
    }
  }

  if (refreshToken) {
    try {
      await supabase.auth.refreshSession({ refresh_token: refreshToken });
    } catch (error) {
      console.error('Failed to restore admin session:', error);
      await supabase.auth.signOut();
    }
  } else {
    await supabase.auth.signOut();
  }

  window.sessionStorage.removeItem(IMPERSONATION_KEYS.refreshToken);
  window.sessionStorage.removeItem(IMPERSONATION_KEYS.accessToken);
  window.sessionStorage.removeItem(IMPERSONATION_KEYS.userId);
  window.sessionStorage.removeItem(IMPERSONATION_KEYS.userLabel);

  // Hard reload to clear any stale Pinia state from the impersonated user.
  window.location.href = '/platform';
}
